// src/pages/admin/AdminConfiguracion.js
//
// Configuración global del sistema (precios, impuestos, retenciones,
// comisiones, reglas). Editable con historial preservado.

import React, { useEffect, useState } from 'react';
import {
  Container, Card, CardContent, Typography, Box, Stack, TextField,
  Button, Alert, Divider, LinearProgress, Grid, IconButton, Tooltip,
} from '@mui/material';
import SettingsIcon  from '@mui/icons-material/Settings';
import SaveIcon      from '@mui/icons-material/Save';
import HistoryIcon   from '@mui/icons-material/History';
import { configuracion as api } from '../../api/tesoreriaApi';
import { useApp } from '../../context/AppContext';

const nombreCategoria = {
  PRECIOS: 'Precios de productos',
  IMPUESTOS: 'Impuestos',
  RETENCIONES: 'Retenciones',
  COMISIONES: 'Comisiones',
  REGLAS: 'Reglas operativas',
  FACTURACION: 'Facturación',
};

export default function AdminConfiguracion() {
  const { notificar } = useApp();
  const [grupos, setGrupos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [valores, setValores] = useState({});    // clave → valor local
  const [guardando, setGuardando] = useState({});// clave → boolean

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await api.listar();
      setGrupos(r || []);
      const v = {};
      (r || []).forEach(g => g.items.forEach(i => { v[i.clave] = i.valor; }));
      setValores(v);
    } catch (e) {
      notificar(e.mensajeUsuario || 'No se pudo cargar la configuración.', 'error');
    } finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const guardar = async (item) => {
    if (valores[item.clave] === item.valor) return;
    setGuardando(g => ({ ...g, [item.clave]: true }));
    try {
      await api.actualizar(item.clave, valores[item.clave]);
      notificar('Cambio guardado.', 'success');
      cargar();
    } catch (e) {
      notificar(e.mensajeUsuario || 'No se pudo guardar.', 'error');
    } finally {
      setGuardando(g => ({ ...g, [item.clave]: false }));
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <SettingsIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontFamily="Fraunces">Configuración del sistema</Typography>
          <Typography variant="body2" color="text.secondary">
            Parámetros globales. Los cambios preservan el historial para no afectar cálculos pasados.
          </Typography>
        </Box>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Los precios se guardan con IVA incluido. Al calcular comisiones se usa la base gravable (sin IVA).
      </Alert>

      {cargando ? <LinearProgress /> : grupos.map(g => (
        <Card key={g.categoria} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontFamily="Fraunces" gutterBottom>
              {nombreCategoria[g.categoria] || g.categoria}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {g.items.map(i => (
                <Grid item xs={12} md={6} key={i.clave}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth size="small"
                      label={i.clave.replace(/_/g, ' ')}
                      value={valores[i.clave] ?? ''}
                      onChange={e => setValores(v => ({ ...v, [i.clave]: e.target.value }))}
                      helperText={i.descripcion || `Tipo: ${i.tipo}`}
                    />
                    <Tooltip title="Guardar cambio">
                      <span>
                        <IconButton color="primary"
                          onClick={() => guardar(i)}
                          disabled={guardando[i.clave] || valores[i.clave] === i.valor}>
                          <SaveIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Ver historial">
                      <IconButton size="small" onClick={async () => {
                        const h = await api.historico(i.clave);
                        alert(h.map(x => `${x.vigenteDesde?.substring(0,10)} → ${x.valor}`).join('\n'));
                      }}>
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Container>
  );
}
